import { useMemo } from "react";
import {
  ActionIcon,
  Badge,
  Group,
  Loader,
  Paper,
  Stack,
  Text,
  Tooltip,
} from "@mantine/core";
import { IconEdit } from "@tabler/icons-react";

interface CaseSummaryProps {
  caseNumber?: string;
  facilityName?: string;
  isBackfillCaseNumber?: boolean;
  isAssigningCaseNumber?: boolean;
  isCaseNumberUnverified?: boolean;
  onEditCaseNumber?: () => void;
}

export default function CaseSummary({
  caseNumber,
  facilityName = "Gulf Coast Pathologists",
  isBackfillCaseNumber = false,
  isAssigningCaseNumber = false,
  isCaseNumberUnverified = false,
  onEditCaseNumber,
}: CaseSummaryProps) {
  const showBackfillContext = useMemo(
    () =>
      isBackfillCaseNumber && !isAssigningCaseNumber && !isCaseNumberUnverified,
    [isAssigningCaseNumber, isBackfillCaseNumber, isCaseNumberUnverified]
  );

  return (
    <Paper p="sm" withBorder>
      <Stack gap="sm">
        <div>
          <Group justify="space-between" gap="xs">
            <Text size="xs" c="dimmed" tt="uppercase" fw={700}>
              Case Number
            </Text>
            {showBackfillContext && (
              <Badge color="teal" variant="light" size="xs">
                Backfill
              </Badge>
            )}
          </Group>
          {isCaseNumberUnverified ? (
            <Text size="sm" c="red">
              Reload to verify the case number
            </Text>
          ) : isAssigningCaseNumber ? (
            <Group gap="xs">
              <Loader size="xs" />
              <Text size="sm" c="dimmed">
                Assigning next case number…
              </Text>
            </Group>
          ) : (
            <Group gap="xs" wrap="nowrap">
              <Text size="lg" fw={600} c="blue">
                {caseNumber || "Loading..."}
              </Text>
              {onEditCaseNumber && (
                <Tooltip label="Correct case number">
                  <ActionIcon
                    variant="subtle"
                    size="lg"
                    aria-label="Correct case number"
                    onClick={onEditCaseNumber}
                  >
                    <IconEdit size={18} />
                  </ActionIcon>
                </Tooltip>
              )}
            </Group>
          )}
          {showBackfillContext && (
            <Text size="xs" c="dimmed">
              Earlier available number reused
            </Text>
          )}
        </div>

        <div>
          <Text size="xs" c="dimmed" tt="uppercase" fw={700}>
            Facility
          </Text>
          <Text size="sm">{facilityName}</Text>
        </div>
      </Stack>
    </Paper>
  );
}
